import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import ClearIcon from '@mui/icons-material/Clear';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import { IconButton, Typography, TextField, Fab, Tooltip } from '@mui/material';
import { ReactComponent as IconeMartin } from '../../../assets/img/icone-martin.svg';
import MensagemAssistente from './MensagemAssistente/MensagemAssistente';
import StatusProcessamento from './StatusProcessamento/StatusProcessamento';
import ListaConversas from './ListaConversas/ListaConversas';
import EstadoVazio from './EstadoVazio/EstadoVazio';
import useChatIAHook from './useChatIAHook';
import useSugestoesHook from './useSugestoesHook';
import SugestoesInput from './SugestoesInput/SugestoesInput';
import './styles.scss';

export default function ChatIA({ chatIaOpen, setChatIaOpen }) {
  const {
    mensagens,
    textoEntrada,
    setTextoEntrada,
    enviando,
    erro,
    conversas,
    uuidConversa,
    mostrandoLista,
    statusProcessamento,
    nomeUsuario,
    refFimMensagens,
    refListaMensagens,
    aoRolarMensagens,
    alternarLista,
    abrirConversa,
    novaConversa,
    enviarMensagem
  } = useChatIAHook({ chatIaOpen });
  const sugestoesEntrada = useSugestoesHook(textoEntrada);
  const sugestoesEstadoVazio = useSugestoesHook('', {
    buscarSemTermo: chatIaOpen && mensagens.length === 0 && !enviando
  });

  // Memoizado porque vai como prop para MensagemAssistente, que e React.memo:
  // uma funcao nova a cada render anularia o memo e faria TODAS as bolhas
  // re-renderizarem a cada delta do streaming.
  const fecharChat = useCallback(() => setChatIaOpen(false), [setChatIaOpen]);

  const aoPressionarTecla = e => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      sugestoesEntrada.moverSelecao(e.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (sugestoesEntrada.sugestaoSelecionada) {
        enviarMensagem(sugestoesEntrada.sugestaoSelecionada.texto);
        sugestoesEntrada.limpar();
        return;
      }
      enviarMensagem();
      sugestoesEntrada.limpar();
    }

    if (e.key === 'Escape') {
      sugestoesEntrada.limpar();
    }
  };

  const aoEscolherSugestao = texto => {
    sugestoesEntrada.limpar();
    enviarMensagem(texto);
  };

  return (
    <>
      <Tooltip title="One, assistente de IA" placement="left">
        <Fab className="chat-ia-fab" onClick={() => setChatIaOpen(true)} aria-label="Abrir o One">
          <IconeMartin className="chat-ia-fab-icone" />
        </Fab>
      </Tooltip>

      <Drawer className="chat-ia-sidebar" open={chatIaOpen} onClose={fecharChat} anchor="right">
        <Box role="presentation" className="chat-ia-container">
          <Box className="chat-ia-header">
            <Box className="chat-ia-marca">
              <Box className="chat-ia-marca-icone" aria-hidden="true">
                <IconeMartin />
              </Box>
              <Box className="chat-ia-marca-texto">
                <Typography className="chat-ia-marca-titulo">One</Typography>
                <Box className="chat-ia-marca-status">
                  <span className="chat-ia-dot-online" aria-hidden="true" />
                  <Typography variant="caption">Online · responde em segundos</Typography>
                </Box>
              </Box>
            </Box>

            <Box className="chat-ia-header-acoes">
              <Tooltip title="Conversas recentes">
                <span>
                  <IconButton
                    className="chat-ia-btn-header"
                    onClick={alternarLista}
                    disabled={conversas.length === 0}
                  >
                    <HistoryIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Nova conversa">
                <span>
                  <IconButton
                    className="chat-ia-btn-header"
                    onClick={novaConversa}
                    disabled={mensagens.length === 0 && !uuidConversa && !mostrandoLista}
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Fechar">
                <IconButton className="chat-ia-btn-header btn-close" onClick={fecharChat}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {mostrandoLista && conversas.length > 0 && (
            <ListaConversas
              conversas={conversas}
              uuidAtivo={uuidConversa}
              aoSelecionar={abrirConversa}
            />
          )}

          <Box className="chat-ia-mensagens" ref={refListaMensagens} onScroll={aoRolarMensagens}>
            {mensagens.map((mensagem, index) =>
              mensagem.papel === 'user' ? (
                <Box key={`${mensagem.papel}-${index}`} className="chat-ia-mensagem-user">
                  <Typography variant="body2">{mensagem.conteudo}</Typography>
                </Box>
              ) : (
                <MensagemAssistente
                  key={`${mensagem.papel}-${index}`}
                  mensagem={mensagem}
                  aoNavegar={fecharChat}
                />
              )
            )}

            {enviando && <StatusProcessamento status={statusProcessamento ?? { etapa: 'pensando' }} />}

            {mensagens.length === 0 && !enviando && (
              <EstadoVazio
                nomeUsuario={nomeUsuario}
                aoEscolherSugestao={aoEscolherSugestao}
                sugestoes={
                  sugestoesEstadoVazio.sugestoes.length > 0 ? sugestoesEstadoVazio.sugestoes : undefined
                }
              />
            )}

            {/* Sentinela do auto-scroll: precisa ser sempre o ultimo filho. */}
            <div ref={refFimMensagens} />
          </Box>

          {erro && (
            <Typography variant="caption" className="chat-ia-erro">
              {erro}
            </Typography>
          )}

          <Box className="chat-ia-entrada">
            <SugestoesInput
              sugestoes={sugestoesEntrada.sugestoes}
              indiceSelecionado={sugestoesEntrada.indiceSelecionado}
              aoSelecionar={aoEscolherSugestao}
            />
            <Box className="chat-ia-pill">
              <TextField
                className="chat-ia-input"
                placeholder="Digite sua pergunta..."
                multiline
                maxRows={4}
                fullWidth
                size="small"
                value={textoEntrada}
                onChange={e => setTextoEntrada(e.target.value)}
                onKeyDown={aoPressionarTecla}
                onBlur={() => setTimeout(sugestoesEntrada.limpar, 150)}
                aria-controls={
                  sugestoesEntrada.sugestoes.length > 0 ? 'chat-ia-sugestoes-lista' : undefined
                }
                aria-activedescendant={
                  sugestoesEntrada.indiceSelecionado === null
                    ? undefined
                    : `chat-ia-sugestao-${sugestoesEntrada.indiceSelecionado}`
                }
                disabled={enviando}
              />
              {/* Arrow function e nao {enviarMensagem}: o onClick passaria o
                  evento como primeiro argumento da funcao. */}
              <IconButton
                className="chat-ia-btn-enviar"
                onClick={() => enviarMensagem()}
                disabled={enviando || !textoEntrada.trim()}
              >
                <SendIcon />
              </IconButton>
            </Box>

            <Typography variant="caption" className="chat-ia-rodape">
              As respostas podem conter imprecisões. Confira dados críticos no sistema.
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
